import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import axios from "src/utils/axios";
import ScanProgressPanel from "src/components/ScanProgressPanel";

// Clave localStorage para persistir el `jobId` del último escaneo iniciado.
// Al refrescar la página el front pierde el estado en memoria; con esta clave
// el dialog puede re-adoptar un job todavía en PENDING/RUNNING en vez de
// volver a pedir imágenes (bug: "me vuelve a preguntar teniendo un escaneo
// pendiente"). También la exportamos para que el host component pueda
// detectar la existencia del pendiente y auto-abrir el dialog tras F5.
export const CARTA_SCAN_PENDING_JOB_KEY = "carta-scan:pendingJobId";
export const getPendingCartaScanJobId = () => {
  try {
    return window.localStorage?.getItem(CARTA_SCAN_PENDING_JOB_KEY) || null;
  } catch {
    return null;
  }
};

// Dialog unificado "Escanear carta con IA" — el mismo que usa el
// BulkProductEditor. Encapsula:
//   1. Selector multi-archivo (imágenes/PDF).
//   2. Compresión previa client-side (max 1600px, JPEG q=0.82) para
//      evitar 413 del backend con fotos de móvil grandes.
//   3. POST /ReadCarta/claude con timeout largo y skipDedupe (el body
//      base64 puede pesar decenas de MB).
//   4. Panel de progreso en vivo (`ScanProgressPanel`) mientras Claude
//      trabaja — feedback continuo al usuario.
//   5. Callback `onScanned(result)` con el `ClaudeCartaScanResult`
//      cuando el user pulsa "Añadir al editor". El caller decide qué
//      hacer con el resultado (mapear a filas del bulk editor,
//      redirigir a otra vista, etc).
export default function ClaudeScanCartaDialog({
  open,
  onClose,
  onScanned,
  title = "Escanear carta con IA",
  description = "Sube fotos o PDFs de tu carta. Claude analizará el texto y detectará productos, complementos y categorías. Los que ya tengas en tu catálogo se marcarán como coincidencia — al aplicar podrás revisar antes de guardar.",
  applyLabel = "Añadir al editor",
  // Cuando true, en cuanto el escaneo termine se auto-aplica el
  // resultado (llama onScanned) y se cierra el modal — sin requerir
  // pulsar "Añadir al editor". Usado por el wizard onboarding para
  // llevar al user directo a la lista con los productos ya cargados.
  autoApplyOnComplete = false,
}) {
  const [scanFiles, setScanFiles] = useState([]);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState(null);
  // Progreso del scan — se rellena con el `progress` que devuelve el
  // polling GET /jobs/{id} (TaskMicro lo actualiza tras cada chunk).
  // Reemplaza al hook SignalR anterior: TaskMicro no tiene hub y no
  // podría emitir eventos push al front, así que hacemos polling REST.
  const [scanProgress, setScanProgress] = useState(null);
  const autoAppliedRef = useRef(false);
  // Timestamp de arranque del polling — se usa para el backoff adaptativo.
  const pollStartRef = useRef(0);

  const compressImageIfNeeded = async (file) => {
    const isImage = /^image\//.test(file.type);
    if (!isImage || file.size < 1024 * 1024) return file;
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const maxW = 1600;
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) return resolve(file);
            const compressed = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressed.size < file.size ? compressed : file);
          },
          "image/jpeg",
          0.82,
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      img.src = url;
    });
  };

  // Ref al setTimeout del polling — permite cancelarlo al desmontar,
  // cerrar el dialog, o cuando el job entra en estado terminal.
  // Antes usábamos setInterval; ahora setTimeout encadenado para
  // aplicar el backoff adaptativo dinámicamente en cada tick.
  const pollTimerRef = useRef(null);
  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => () => clearPollTimer(), []); // cleanup al desmontar

  const persistJob = (jobId) => {
    try {
      if (jobId) window.localStorage.setItem(CARTA_SCAN_PENDING_JOB_KEY, String(jobId));
      else window.localStorage.removeItem(CARTA_SCAN_PENDING_JOB_KEY);
    } catch { /* private mode */ }
  };

  // Polling adaptativo — backoff progresivo + pausa cuando la pestaña
  // está oculta. Reduce presión sobre el backend cuando el escaneo se
  // alarga (típicamente 30-120s) o el user cambia de pestaña.
  //   • 0-15s: 1500ms  (feedback rápido inicial)
  //   • 15-60s: 3000ms (pace normal)
  //   • >60s:  5000ms  (escaneos largos → menos ruido)
  //   • document.hidden: NO dispara tick (reanuda al foco)
  const pollDelayFor = (elapsedMs) => {
    if (elapsedMs < 15_000) return 1500;
    if (elapsedMs < 60_000) return 3000;
    return 5000;
  };

  const scheduleNextPoll = (jobId) => {
    const elapsed = Date.now() - pollStartRef.current;
    const delay = pollDelayFor(elapsed);
    pollTimerRef.current = setTimeout(() => runPollTick(jobId), delay);
  };

  const runPollTick = async (jobId) => {
    // Si la pestaña está en background, saltamos este tick — reagendamos
    // con el mismo delay para reintentar cuando vuelva a estar visible.
    if (typeof document !== "undefined" && document.hidden) {
      scheduleNextPoll(jobId);
      return;
    }
    try {
      const res = await axios.get(`ReadCarta/claude/jobs/${jobId}`);
      const job = res.data || {};
      // Progreso en vivo — reemplaza los eventos SignalR del flujo
      // antiguo. TaskMicro actualiza CartaScanJob.ProgressJson tras
      // cada chunk; el DTO lo devuelve como `progress`.
      if (job.progress) setScanProgress(job.progress);
      if (job.status === "COMPLETED") {
        clearPollTimer();
        persistJob(null);
        setScanResult(job.result || null);
        setScanBusy(false);
        return;
      }
      if (job.status === "FAILED") {
        clearPollTimer();
        persistJob(null);
        setScanError(job.errorMessage || "Error escaneando la carta");
        setScanBusy(false);
        return;
      }
      // PENDING / RUNNING → siguiente tick.
      scheduleNextPoll(jobId);
    } catch (err) {
      if (err?.response?.status === 404) {
        clearPollTimer();
        persistJob(null);
        setScanError("El job de escaneo ya no está disponible.");
        setScanBusy(false);
        return;
      }
      // Errores transitorios (red flaky, 5xx) → reintenta al próximo tick.
      scheduleNextPoll(jobId);
    }
  };

  const pollJob = (jobId) => {
    clearPollTimer();
    persistJob(jobId);
    pollStartRef.current = Date.now();
    setScanProgress(null);
    // Primer tick inmediato para no esperar 1500ms tras encolar.
    runPollTick(jobId);
  };

  // Al abrir el dialog: si hay `jobId` pendiente en localStorage
  // (F5 durante escaneo, cierre voluntario del modal, reinicio del
  // wizard, cambio de vista), lo re-adoptamos SIN pedir imágenes
  // de nuevo. Ponemos `scanBusy=true` INMEDIATAMENTE para que el
  // usuario vea el spinner + panel de progreso mientras el axios
  // resuelve — sin gap "modal vacío" al reabrir.
  useEffect(() => {
    if (!open || scanBusy || scanResult) return;
    const pending = getPendingCartaScanJobId();
    if (!pending) return;
    // Feedback instantáneo: mostramos "Analizando…" mientras
    // consultamos el estado real del job.
    setScanBusy(true);
    setScanError(null);
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`ReadCarta/claude/jobs/${pending}`);
        if (cancelled) return;
        const job = res.data || {};
        if (job.status === "COMPLETED") {
          setScanResult(job.result || null);
          setScanBusy(false);
          persistJob(null);
        } else if (job.status === "FAILED") {
          setScanError(job.errorMessage || "El escaneo anterior falló.");
          setScanBusy(false);
          persistJob(null);
        } else {
          // PENDING / RUNNING → reengancha el polling. `scanBusy` ya
          // está true; el ScanProgressPanel viene por SignalR.
          pollJob(pending);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          // El job ya no existe (limpieza, otro tenant, etc.).
          persistJob(null);
          setScanBusy(false);
        } else {
          // Error transitorio (red, 5xx): dejamos `scanBusy=true` y el
          // polling se encargará; si tampoco funciona, mostrará el
          // error. Sin ruido para el usuario en el caso normal.
          pollJob(pending);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleScanRun = async () => {
    if (!scanFiles || scanFiles.length === 0) return;
    setScanBusy(true);
    setScanError(null);
    setScanResult(null);
    try {
      // Compresión previa SOLO para imágenes (fotos de móvil grandes).
      // PDFs, textos y otros formatos van tal cual — Kestrel bufferea
      // al disco al parsear el multipart, así no explota la RAM del API
      // por más grande que sea el archivo.
      const optimized = await Promise.all(scanFiles.map(compressImageIfNeeded));

      // Nuevo endpoint MULTIPART — cada archivo se streamea directo al
      // Blob Storage. Sustituye al viejo /claude/async que recibía
      // base64 en JSON y disparaba la RAM del API a >1GB con PDFs de
      // pocos MB. Ver CartaScanInputStorage + endpoint claude/upload.
      const formData = new FormData();
      optimized.forEach((f) => formData.append("files", f, f.name));
      const res = await axios.post(
        "ReadCarta/claude/upload",
        formData,
        {
          skipDedupe: true,
          maxContentLength: 500 * 1024 * 1024,
          maxBodyLength: 500 * 1024 * 1024,
          // NO fijar Content-Type manualmente — axios necesita
          // calcular el boundary del multipart automáticamente.
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      const jobId = res?.data?.jobId;
      if (!jobId) throw new Error("El servidor no devolvió jobId");
      pollJob(jobId);
    } catch (err) {
      setScanError(
        err?.response?.data?.message
          || err?.message
          || "Error escaneando la carta",
      );
      setScanBusy(false);
    }
  };

  const handleApply = () => {
    if (!scanResult) return;
    try {
      onScanned?.(scanResult);
    } catch { /* consumer bug */ }
    // Reset local antes de que el caller cierre — evita ver el estado
    // viejo si vuelve a abrir sin desmontar. Al aplicar SIEMPRE se
    // consume el job pendiente (ya no queremos re-adoptarlo).
    persistJob(null);
    setScanFiles([]);
    setScanResult(null);
    setScanError(null);
  };

  // Cancel al cerrar pestaña — `navigator.sendBeacon` garantiza el
  // POST aunque el navegador esté cerrándose (fetch normal se
  // aborta). El backend marca el job CANCELLED, libera el worker
  // del OutboxHeavyConsumer y no procesa un scan cuyo user ya se
  // fue. Sin esto TaskMicro procesaba scans "muertos" gastando
  // tokens Claude para nada.
  useEffect(() => {
    if (!open || !scanBusy) return undefined;
    const handleBeforeUnload = () => {
      const pending = getPendingCartaScanJobId();
      if (!pending) return;
      try {
        const url = `${axios.defaults.baseURL || ""}ReadCarta/claude/jobs/${pending}/cancel`;
        // Auth: sendBeacon no acepta headers, pero el backend acepta el
        // JWT que viaja en la cookie de sesión — el interceptor axios
        // añade Authorization en cada request. Como no podemos poner
        // headers en beacon, usamos un endpoint que acepte cookie auth
        // (cookieContainer HttpOnly del refresh token) o token en URL
        // como fallback. Para simplificar: aceptamos que el cancel
        // pueda fallar por auth — el auto-heal del backend limpia el
        // job en <=15min de todos modos. Mejor este best-effort que
        // nada.
        navigator.sendBeacon?.(url, new Blob([], { type: "application/json" }));
      } catch { /* nada que hacer si el beacon falla */ }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [open, scanBusy]);

  // Auto-apply + auto-close cuando el caller lo pide y hay resultado.
  // Usado en el wizard onboarding — el user no tiene que pulsar
  // "Añadir al editor", los productos se aplican directamente y el
  // modal se cierra revelando la vista lista con los productos ya
  // cargados. Sin esto el user veía "escaneo completado" y se cerraba
  // sin nada visible.
  //
  // Guard "0 productos": si Claude no detectó nada legible (foto
  // borrosa, PDF vacío, etc.), NO auto-cerramos. Dejamos el Alert
  // "Detectados 0 productos" visible para que el user reintente. Sin
  // este guard el modal se cerraba y el user pensaba que el scan
  // había fallado en silencio.
  useEffect(() => {
    if (!autoApplyOnComplete) return;
    if (!scanResult) return;
    if (autoAppliedRef.current) return;

    const totalProducts = (scanResult.categories || [])
      .reduce((sum, c) => sum + (c?.productos?.length || 0), 0);
    if (totalProducts === 0) {
      // Marcado como aplicado para no re-evaluar en cada re-render,
      // pero SIN llamar onScanned ni cerrar. El user ve el Alert y
      // decide: reintentar o cerrar manualmente.
      autoAppliedRef.current = true;
      return;
    }

    autoAppliedRef.current = true;
    try {
      onScanned?.(scanResult);
    } catch { /* consumer bug */ }
    persistJob(null);
    setScanFiles([]);
    setScanResult(null);
    setScanError(null);
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanResult, autoApplyOnComplete]);

  // Reset del flag auto-applied al reabrir el modal para permitir un
  // nuevo escaneo dentro de la misma sesión.
  useEffect(() => {
    if (open) autoAppliedRef.current = false;
  }, [open]);

  const handleClose = () => {
    // Cancelar SIEMPRE permitido — el worker de backend sigue procesando
    // en background aunque el usuario cierre. Desenganchamos el polling
    // + reseteamos estado local, pero DEJAMOS el jobId en localStorage
    // para que al reabrir el modal (o al recargar la página, o al
    // reiniciar el wizard) el `useEffect` de re-adopción lo detecte y
    // muestre el progreso/resultado sin volver a pedir imágenes.
    //
    // El jobId solo se limpia en 3 sitios:
    //   1) `handleApply` — el user aplicó el resultado (consumido).
    //   2) polling recibe FAILED / 404 — ya no hay nada útil.
    //   3) el user re-escanea desde cero (`handleScanRun` no lo toca,
    //      pero `pollJob` sobrescribe con el nuevo jobId).
    clearPollTimer();
    setScanFiles([]);
    setScanResult(null);
    setScanError(null);
    setScanBusy(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={scanBusy}
          >
            Seleccionar archivos ({scanFiles.length})
            <input
              type="file"
              // Aceptamos cualquier archivo — el backend decide qué
              // hacer con cada uno según su Content-Type: image/* →
              // bloque imagen, application/pdf → documento, text/* /
              // csv / json → bloque de texto. El resto se ignora con
              // warning en el log.
              accept="image/*,application/pdf,text/plain,text/csv,application/json,.txt,.csv,.json,.md"
              multiple
              hidden
              onChange={(e) => setScanFiles(Array.from(e.target.files || []))}
            />
          </Button>
          {scanFiles.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {scanFiles.map((f) => f.name).join(", ")}
            </Typography>
          )}
          {scanBusy && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: "background.neutral",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <ScanProgressPanel
                enabled={scanBusy}
                progress={scanProgress}
              />
            </Box>
          )}
          {scanError && <Alert severity="error">{scanError}</Alert>}
          {scanResult && (
            <Alert severity="success">
              Detectados <b>{scanResult.meta?.totalProductsDetected ?? 0}</b>{" "}
              productos en{" "}
              <b>{scanResult.categories?.length ?? 0}</b> categorías.
              {scanResult.meta?.matchedProducts > 0 && (
                <> · <b>{scanResult.meta.matchedProducts}</b> ya existen en tu catálogo.</>
              )}
              {scanResult.meta?.lowConfidenceCount > 0 && (
                <> · <b>{scanResult.meta.lowConfidenceCount}</b> con baja confianza (revisa manualmente).</>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {scanBusy ? "Cerrar (seguirá en segundo plano)" : "Cancelar"}
        </Button>
        {!scanResult ? (
          <Button
            variant="contained"
            onClick={handleScanRun}
            disabled={scanBusy || scanFiles.length === 0}
          >
            {scanBusy ? "Analizando…" : "Analizar"}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleApply}
          >
            {applyLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ClaudeScanCartaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Recibe el ClaudeCartaScanResult ({ categories, meta }) cuando el user pulsa "Añadir". */
  onScanned: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  applyLabel: PropTypes.string,
  autoApplyOnComplete: PropTypes.bool,
};
